/**
 * Centralized API Endpoints
 * Single source of truth for all backend routes
 * 
 * Usage:
 * import { API_ENDPOINTS } from '@/api/endpoints';
 * apiClient.get(API_ENDPOINTS.MEMBERS.BASE);
 */

export const API_ENDPOINTS = {
  // ============================================
  // AUTHENTICATION
  // ============================================
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    TWO_FA_VALIDATE: '/auth/2fa/validate',
  },

  // ============================================
  // USERS (Admin Profile & Team)
  // ============================================
  USERS: {
    ADMINS: '/users/admins',
    ME: '/users/me',
    PASSWORD: '/users/me/password',
    STATUS: (id: string) => `/users/${id}/status`,
    ADMIN_RESET_PASSWORD: (id: string) => `/users/${id}/reset-password-admin`,
    DELETE: (id: string) => `/users/${id}`,
  },

  // ============================================
  // MEMBERS (Agents of Transformation)
  // ============================================
  MEMBERS: {
    BASE: '/members',
    REGISTER: '/members/register',
    EXPORT: '/members/export',
    BY_ID: (id: string) => `/members/${id}`,
    APPROVE: (id: string) => `/members/${id}/approve`,
    WAITLIST: (id: string) => `/members/${id}/waitlist`,
    STATUS: (id: string) => `/members/${id}/status`,
  },

  // ============================================
  // ACTIVITIES
  // ============================================
  ACTIVITIES: {
    BASE: '/activities',
    BY_ID: (id: number) => `/activities/${id}`,
  },

  // ============================================
  // BLOG
  // ============================================
  BLOG: {
    BASE: '/blog',
    EXPORT: '/blog/export',
    BY_SLUG: (slug: string) => `/blog/${slug}`,
    BY_ID: (id: string) => `/blog/${id}`,
    IMAGE: (id: string) => `/blog/${id}/image`,
    PUBLISH: (id: string) => `/blog/${id}/publish`,
  },

  // ============================================
  // DONATIONS
  // ============================================
  DONATIONS: {
    BASE: '/donations',
    EXPORT: '/donations/export',
    STATS: '/donations/stats',
    BY_ID: (id: string) => `/donations/${id}`,
  },

  // ============================================
  // CONTACTS
  // ============================================
  CONTACTS: {
    BASE: '/contacts',
    EXPORT: '/contacts/export',
    STATS: '/contacts/stats',
    MARK_READ: (id: number) => `/contacts/${id}/read`,
    SEND_MAIL: '/contacts/send-mail',
  },

  // ============================================
  // SUBSCRIBERS (Newsletter)
  // ============================================
  SUBSCRIBERS: {
    BASE: '/subscribers',
    UNSUBSCRIBE: (token: string) => `/subscribers/unsubscribe/${token}`,
    STATS: '/subscribers/stats',
    SEND_NEWSLETTER: '/subscribers/send-newsletter',
    TOGGLE: (id: string) => `/subscribers/${id}/toggle`,
  },

  // ============================================
  // PRINCIPLES (10 Leadership Principles)
  // ============================================
  PRINCIPLES: {
    BASE: '/principles',
    BY_ID: (id: number) => `/principles/${id}`,
  },

  // ============================================
  // SETTINGS
  // ============================================
  SETTINGS: {
    BASE: '/settings',
    PUBLIC: '/settings/public',
    INSPIRATION: '/settings/inspiration',
    BY_KEY: (key: string) => `/settings/${key}`,
    SOCIAL: {
      FEED: '/settings/social/feed',
      INSTAGRAM: '/settings/social/instagram',
      X: '/settings/social/x',
    },
    INSTAGRAM_REFRESH: '/settings/instagram/refresh',
    CONTACT_NOTIFICATIONS: '/settings/contact-notifications',
  },

  // ============================================
  // INSTAGRAM (Social Media)
  // ============================================
  INSTAGRAM: {
    FEED: '/settings/social/instagram',
    ADMIN: '/settings/instagram/admin',
    HIDE: (postId: string) => `/settings/instagram/hide/${postId}`,
    SHOW: (postId: string) => `/settings/instagram/show/${postId}`,
    VISIBILITY: '/settings/instagram/visibility',
    REFRESH_TOKEN: '/settings/instagram/refresh',
  },

  // ============================================
  // STATS (Dashboard & Public)
  // ============================================
  STATS: {
    PUBLIC: '/stats/public',
    DASHBOARD: '/stats/dashboard',
  },

  // ============================================
  // MEDIA (File Uploads)
  // ============================================
  MEDIA: {
    UPLOAD: '/media/upload',
    DELETE: '/media/delete',
  },

  // ============================================
  // AUDIT LOGS
  // ============================================
  AUDIT: {
    BASE: '/audit-logs',
    STATS: '/audit-logs/stats',
    EXPORT_EXCEL: '/audit-logs/export-excel',
    EXPORT_JSON: '/audit-logs/export-json',
    PURGE_AND_ARCHIVE: '/audit-logs/purge-and-archive',
    /** @deprecated Use PURGE_AND_ARCHIVE instead */
    EXPORT_FLUSH: '/audit-logs/export-and-flush',
    /** Client-side error reporting endpoint (public) */
    CLIENT_ERRORS: '/audit-logs/client-errors',
  },

  // ============================================
  // HEALTH CHECK
  // ============================================
  HEALTH: {
    CHECK: '/health',
    LOGS: '/health/logs',
    BACKUP: '/health/backup',
    EMAIL_QUEUE_STATS: '/health/email-queue/stats',
    EMAIL_QUEUE: '/health/email-queue',
    EMAIL_QUEUE_RETRY: (id: string) => `/health/email-queue/${id}/retry`,
    EMAIL_QUEUE_RETRY_ALL: '/health/email-queue/retry-all',
  },

  // ============================================
  // SCHOOLS (Club Management)
  // ============================================
  SCHOOLS: {
    BASE: '/schools',
    EXPORT: '/schools/export',
    PUBLIC: '/schools/public',
    STATS: '/schools/stats',
    AGENTS: '/schools/agents',
    BY_ID: (id: number) => `/schools/${id}`,
  },

  // ============================================
  // TEAM (Notre Équipe)
  // ============================================
  TEAM: {
    BASE: '/team',
    PUBLIC: '/team/public',
    REORDER: '/team/reorder',
    BY_ID: (id: number) => `/team/${id}`,
  },

  // ============================================
  // EVENTS
  // ============================================
  EVENTS: {
    BASE: '/events',
    EXPORT: '/events/export',
    BY_ID: (id: string) => `/events/${id}`,
    BY_SLUG: (slug: string) => `/events/${slug}`,
    PUBLISH: (id: string) => `/events/${id}/publish`,
    RSVP: (id: string) => `/events/${id}/rsvp`,
    CLOSE: (id: string) => `/events/${id}/close`,
  },

  // ============================================
  // BOOKINGS
  // ============================================
  BOOKINGS: {
    BASE: '/bookings',
    EXPORT: '/bookings/export',
    BY_ID: (id: string) => `/bookings/${id}`,
    EVENT_STATS: (eventId: string) => `/bookings/event/${eventId}/stats`,
    APPROVE: (id: string) => `/bookings/${id}/approve`,
    REJECT: (id: string) => `/bookings/${id}/reject`,
    CHECK_IN: (id: string) => `/bookings/${id}/check-in`,
    VERIFY: (token: string) => `/bookings/verify/${token}`,
    DOWNLOAD_TICKET: (id: string) => `/bookings/${id}/download-ticket`,
    DELETE: (id: string) => `/bookings/${id}`,
  },

  // ============================================
  // REGISTRATIONS (Simple event sign-up)
  // ============================================
  REGISTRATIONS: {
    BASE: '/registrations',
    EXPORT: (eventId: string) => `/registrations/export/${eventId}`,
    EVENT_STATS: (eventId: string) => `/registrations/event/${eventId}/stats`,
    LOOKUP: (eventId: string, ticketNumber: number) =>
      `/registrations/event/${eventId}/lookup/${ticketNumber}`,
    ATTENDED: (id: string) => `/registrations/${id}/attended`,
    DELETE: (id: string) => `/registrations/${id}`,
  },

  // ============================================
  // NOTIFICATIONS
  // ============================================
  NOTIFICATIONS: {
    BASE: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    READ: (id: number) => `/notifications/${id}/read`,
    READ_ALL: '/notifications/read-all',
    DELETE: (id: number) => `/notifications/${id}`,
  },

  // ============================================
  // GALLERY
  // ============================================
  GALLERY: {
    BASE: '/gallery',
    PUBLIC: '/gallery/public',
    PUBLIC_BY_ID: (id: number) => `/gallery/public/${id}`,
    BY_ID: (id: number) => `/gallery/${id}`,
    PUBLISH: (id: number) => `/gallery/${id}/publish`,
    PHOTOS: (albumId: number) => `/gallery/${albumId}/photos`,
    PHOTO: (albumId: number, photoId: number) => `/gallery/${albumId}/photos/${photoId}`,
    REORDER_PHOTOS: (albumId: number) => `/gallery/${albumId}/photos/reorder`,
  },

  // ============================================
  // 2FA (Two-Factor Authentication)
  // ============================================
  TWO_FA: {
    STATUS: '/auth/2fa/status',
    SETUP: '/auth/2fa/setup',
    VERIFY: '/auth/2fa/verify',
    DISABLE: '/auth/2fa/disable',
  },

  // ============================================
  // TRASH (Soft Delete Management - SUPER_ADMIN only)
  // ============================================
  TRASH: {
    BASE: '/trash',
    PURGE_PENDING: '/trash/purge-pending',
    RESTORE: (model: string, id: string | number) => `/trash/${model}/${id}/restore`,
    HARD_DELETE: (model: string, id: string | number) => `/trash/${model}/${id}`,
    MANUAL_PURGE: '/trash/purge',
    BULK_RESTORE: '/trash/bulk-restore',
    BULK_DELETE: '/trash/bulk-delete',
  },

  // ============================================
  // YIM FORM (Custom Form Builder)
  // ============================================
  YIM_FORM: {
    BASE: '/yim-form',
    BY_ID: (id: string) => `/yim-form/${id}`,
    SUBMISSIONS: (id: string) => `/yim-form/${id}/submissions`,
    EXPORT: (id: string, format?: 'csv' | 'xlsx') =>
      format === 'csv' ? `/yim-form/${id}/export?format=csv` : `/yim-form/${id}/export`,
    PUBLISH: (id: string) => `/yim-form/${id}/publish`,
    CLOSE: (id: string) => `/yim-form/${id}/close`,
    REOPEN: (id: string) => `/yim-form/${id}/reopen`,
    PUBLIC: (slug: string) => `/yim-form/public/${slug}`,
    PUBLIC_UNLOCK: (slug: string) => `/yim-form/public/${slug}/unlock`,
    PUBLIC_SUBMIT: '/yim-form/public/submit',
  },
} as const;

/**
 * Helper type to extract endpoint values
 */
export type ApiEndpoint = typeof API_ENDPOINTS;

/**
 * Build URL with query parameters
 */
export function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  if (!params) return endpoint;

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

